import React, { useCallback, useMemo } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";
import { Field, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";
import { Skeleton } from "@fronterahealth/frontera-ui-components/ui/skeleton";

import {
  ApiTreatmentPlanTemplateFieldFieldTypeChoices,
  BaseFieldType,
  ReportDomainType,
} from "@api/graphql/types-and-hooks";
import { TreatmentPlanField } from "@components/ReportField/TreatmentPlanField";
import { TreatmentPlanImage } from "@components/ReportField/TreatmentPlanField/inputs/TreatmentPlanImage";
import { SaveFieldsResult } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { getSelectOptionsForField, snakeToCamel } from "@components/TreatmentPlanFieldForm/utils";

interface TreatmentPlanFormProps {
  /** Template fields defining what to render and in what order */
  templateFields: {
    fieldIdentifier: string;
    title?: string | null;
    fieldType: ApiTreatmentPlanTemplateFieldFieldTypeChoices;
    rankOrder: number;
    isRequired: boolean;
    isEnabled: boolean;
    content?: string | null;
    reportGoalField: boolean;
    reportTargetField: boolean;
    domain?: { name?: string | null } | null;
  }[];
  /** Native field values keyed by snake_case field_identifier */
  nativeFieldValues: Record<string, string | null>;
  /** Save callback for native fields. Receives { [camelCaseKey]: value }. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNativeFieldSave: (fields: Record<string, any>) => Promise<SaveFieldsResult>;
  /**
   * Save callback for custom fields. Receives (fieldIdentifier, content,
   * existingCustomFieldId | null). When the existing id is null the caller
   * should upsert by field_identifier so the backend can auto-create the
   * row from the template spec on first save.
   */
  onCustomFieldSave?: (
    fieldIdentifier: string,
    content: string,
    existingCustomFieldId: string | null,
  ) => Promise<SaveFieldsResult>;
  /** Custom field records from the goal/target query */
  customFields?: { id: string; fieldIdentifier: string; content?: string | null; imageSignedUrl?: string | null }[];
  /** Field overrides: ReactNode = render instead, null = skip, absent = default */
  fieldOverrides?: Record<string, React.ReactNode | null>;
  isReportCompleted?: boolean;
  isReadOnly?: boolean;
  itemId?: string;
  domain: ReportDomainType | null;
  getSuggestionCount?: (fieldIdentifier: string) => number;
  /** Called after an image custom field uploads or clears so the consumer can refetch. */
  onImageUploaded?: () => Promise<void> | void;
  /**
   * When set, IMAGE fields render a disabled placeholder with this message
   * instead of the interactive upload widget. Used by the deferred-persistence
   * create editors (reassessment new goal/target), where the backing entity
   * doesn't exist yet so an upload has no custom-field row to attach to.
   */
  imageUploadDisabledMessage?: string;
}

function toBaseFieldType(fieldType: ApiTreatmentPlanTemplateFieldFieldTypeChoices): BaseFieldType {
  return fieldType as unknown as BaseFieldType;
}

export const TreatmentPlanFormSkeleton: React.FC<{ count: number }> = ({ count }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: count })
        .fill(0)
        .map((_, i) => i)
        .map((_) => (
          <div key={_} className="mb-4">
            <Skeleton className="h-4 w-1/2 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
    </div>
  );
};

export const TreatmentPlanForm: React.FC<TreatmentPlanFormProps> = ({
  templateFields,
  nativeFieldValues,
  onNativeFieldSave,
  onCustomFieldSave,
  customFields = [],
  fieldOverrides = {},
  isReportCompleted = false,
  isReadOnly = false,
  itemId,
  domain,
  getSuggestionCount,
  onImageUploaded,
  imageUploadDisabledMessage,
}) => {
  const customFieldMap = useMemo(() => {
    const map = new Map<string, { id: string; content: string; imageSignedUrl: string | null }>();
    for (const cf of customFields) {
      map.set(cf.fieldIdentifier, {
        id: cf.id,
        content: cf.content ?? "",
        imageSignedUrl: cf.imageSignedUrl ?? null,
      });
    }
    return map;
  }, [customFields]);

  const visibleFields = useMemo(
    () =>
      templateFields.filter((f) => {
        // If the field is not enabled, skip it
        if (!f.isEnabled) {
          return false;
        }
        // If the field has a domain and it doesn't match the current domain, skip it
        if (f.domain && f.domain.name && (!domain || domain?.name !== f.domain.name)) {
          return false;
        }
        // Always render enabled template fields — even if the backing
        // CustomField row doesn't exist yet. The backend auto-creates it
        // on first save via createUpdateCustomFieldValue + fieldIdentifier.
        return true;
      }),
    [templateFields, domain],
  );

  const handleNativeSave = useCallback(
    async (fieldIdentifier: string, value: string): Promise<SaveFieldsResult> => {
      return onNativeFieldSave({ [snakeToCamel(fieldIdentifier)]: value });
    },
    [onNativeFieldSave],
  );

  const handleCustomSave = useCallback(
    async (fieldIdentifier: string, value: string): Promise<SaveFieldsResult> => {
      if (!onCustomFieldSave) {
        return { success: false, error: "Custom field save handler is not configured" };
      }
      const existingId = customFieldMap.get(fieldIdentifier)?.id ?? null;
      return onCustomFieldSave(fieldIdentifier, value, existingId);
    },
    [customFieldMap, onCustomFieldSave],
  );

  return (
    <div className="space-y-2">
      {visibleFields.map((field) => {
        const fieldId = field.fieldIdentifier;

        if (fieldId in fieldOverrides) {
          const slot = fieldOverrides[fieldId];
          if (slot === null) return null;
          return <React.Fragment key={fieldId}>{slot}</React.Fragment>;
        }

        // Image fields aren't scalar-value inputs, so they bypass the shared
        // TreatmentPlanField/registry path and render their own upload widget
        // wired to the treatment-plan custom-field file mutations.
        if (field.fieldType === ApiTreatmentPlanTemplateFieldFieldTypeChoices.Image) {
          const existingCustomField = customFieldMap.get(fieldId);
          return (
            <Field key={fieldId} id={fieldId} data-testid={`treatment-plan-field.${fieldId}`}>
              {field.title && (
                <FieldLabel>
                  {field.title}
                  {field.isRequired && (
                    <span aria-label="required" className="text-destructive">
                      *
                    </span>
                  )}
                </FieldLabel>
              )}
              {imageUploadDisabledMessage ? (
                // Deferred-persistence create editors: the goal/target row doesn't
                // exist yet, so there's nothing to attach an uploaded image to.
                // Show a hint instead of the upload widget until the entity is saved.
                <div className="flex items-center justify-center rounded-lg border border-dashed border-limestone-200 bg-limestone-50 px-6 py-8">
                  <Small colorType="secondary" displayType="normal">
                    {imageUploadDisabledMessage}
                  </Small>
                </div>
              ) : (
                <TreatmentPlanImage
                  customFieldId={existingCustomField?.id ?? null}
                  imageSignedUrl={existingCustomField?.imageSignedUrl ?? null}
                  disabled={isReportCompleted || isReadOnly}
                  onEnsureCustomFieldId={async () => {
                    const existingId = customFieldMap.get(fieldId)?.id ?? null;
                    if (existingId) return existingId;
                    const res = await handleCustomSave(fieldId, "");
                    return res.customFieldId ?? null;
                  }}
                  onRemove={async () => {
                    await handleCustomSave(fieldId, "");
                    await onImageUploaded?.();
                  }}
                  onUploaded={() => onImageUploaded?.()}
                />
              )}
            </Field>
          );
        }

        const isNativeField = field.reportGoalField || field.reportTargetField;
        const fieldValue = isNativeField
          ? nativeFieldValues[fieldId] || ""
          : customFieldMap.get(fieldId)?.content || "";
        const onFieldSave = isNativeField ? handleNativeSave : handleCustomSave;

        const suggestionFieldId = itemId ? `${itemId}::${fieldId}` : undefined;
        const suggestionCount = getSuggestionCount ? getSuggestionCount(fieldId) : undefined;
        const suggestionProps =
          suggestionFieldId !== undefined && suggestionCount !== undefined
            ? { suggestionFieldId, suggestionCount }
            : {};

        return (
          <TreatmentPlanField
            key={fieldId}
            fieldIdentifier={fieldId}
            fieldType={toBaseFieldType(field.fieldType)}
            value={fieldValue}
            onSave={onFieldSave}
            title={field.title || ""}
            required={field.isRequired}
            selectOptions={getSelectOptionsForField(fieldId, field.content)}
            isReportCompleted={isReportCompleted}
            isReadOnly={isReadOnly}
            {...suggestionProps}
          />
        );
      })}
    </div>
  );
};
